import { Accordion, accordionClasses } from '@naf-simba/elements-accordion';
import { simbaStyled, type Theme } from '@naf-simba/theme';
import { nafColor } from '@nafcore/theme';
import { isNotTransientOrSystemProp } from '../../../lib/shouldForwardProp';
import { MembershipAccordionVariant } from '../Types';

// neutral3 and neutral1 have no Simba equivalent, so the dimmed titles stay on the NAF palette.
const titleColor = (theme: Theme, variant?: MembershipAccordionVariant) => {
  switch (variant) {
    case MembershipAccordionVariant.Available:
      return theme.palette.simba.colors.common.black;
    case MembershipAccordionVariant.Unavailable:
      return nafColor.neutral.neutral3;
    default:
      return nafColor.neutral.neutral1;
  }
};

const CHECK_ICON =
  "url(\"data:image/svg+xml,%3Csvg width='10' height='8' viewBox='0 0 10 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.18305 0.307678L9.30679 1.61085L4.07014 7.69229L0.691406 3.78279L1.81515 2.47962L4.07014 5.08596L8.18305 0.307678Z' fill='%231a1a1a'/%3E%3C/svg%3E\")";
const MINUS_ICON =
  "url(\"data:image/svg+xml,%3Csvg width='10' height='3' viewBox='0 0 10 2' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='0' y='0.5' width='10' height='1' fill='%23C1C1C1'/%3E%3C/svg%3E\")";

type StyledAccordionProps = {
  $variant?: MembershipAccordionVariant;
  $showIcon?: boolean;
  $noBackground?: boolean;
};

export const StyledAccordion = simbaStyled<typeof Accordion, StyledAccordionProps>(Accordion, {
  shouldForwardProp: isNotTransientOrSystemProp,
})(({ theme, $variant, $showIcon = true, $noBackground }) => ({
  // Badge and title on the left, chevron on the right; the badge sets its own spacing.
  '&& button': {
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 0,
    padding: `${theme.spacing('m')} ${theme.spacing('ml')} ${theme.spacing('m')} ${theme.spacing('l')}`,
    color: titleColor(theme, $variant),
  },

  [`&& .${accordionClasses.chevron}`]: {
    marginLeft: 'auto',
    alignSelf: 'center',
    color: theme.palette.simba.colors.common.black,
  },

  // The availability badge before the title: a check on moss, or a minus in a grey ring.
  ...($showIcon && {
    '&& button::before': {
      content: "''",
      flex: `0 0 ${theme.spacing('ml')}`,
      width: theme.spacing('ml'),
      height: theme.spacing('ml'),
      marginTop: `calc((1.75rem - ${theme.spacing('ml')}) / 2)`,
      marginRight: theme.spacing('m'),
      borderRadius: '50%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: '10px',
      ...($variant === MembershipAccordionVariant.Available
        ? { backgroundColor: theme.palette.simba.colors.secondary[300], backgroundImage: CHECK_ICON }
        : { border: `2px solid ${nafColor.neutral.neutral3}`, backgroundImage: MINUS_ICON }),
    },
  }),

  ...(!$noBackground && {
    "&:has(button[aria-expanded='true'])": {
      backgroundColor: theme.palette.simba.colors.secondary[100],
    },
  }),

  "&& [role='region']": {
    padding: `0 ${theme.spacing('l')} ${theme.spacing('xl')}`,

    'p, ul, li': {
      fontSize: '1rem',
      fontWeight: 500,
    },
  },
}));
